/** Header-action builder for the Documents tab (file grid + upload strip).
 *  Owns: Search morph, View-mode toggle,
 *  Cancel-selection pill, Upload CTA, and the search-active mobile cluster
 *  (view+sort pill + cancel X). Desktop-only gating is preserved for all
 *  management actions; the Upload and search morphs work on every
 *  viewport. No JSX — all actions fire window events that
 *  DocumentManagementTab listens for. */

import {
  Search,
  ArrowUpDown,
  List,
  LayoutGrid,
  CheckSquare,
  Download,
  Trash2,
  FileText,
  Plus,
  FolderPlus,
  ChevronLeft,
  Loader2,
  History,
  Check,
  X,
} from '@/shared/ui/icons';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

export interface DocumentsHeaderState {
  searchQuery: string;
  hasRevisionHistory?: boolean;
  folderPanelOpen?: boolean;
  activeFilter: 'all' | 'latest' | 'all_revisions' | 'superseded' | 'drawings' | 'for_review' | 'starred';
  sortBy: 'name' | 'date' | 'size' | 'type' | 'document_number' | 'revision' | 'status';
  sortDirection?: 'asc' | 'desc';
  viewMode: 'grid' | 'list';
  bulkSelectMode: boolean;
  /** Items selected on desktop; drives the bulk Download and Delete pills. */
  selectedCount?: number;
  canBulkDownload?: boolean;
  canBulkDelete?: boolean;
  canUpload: boolean;
  isUploading: boolean;
  canManagePermissions: boolean;
  showTenderLetter: boolean;
  /** Workspace documents have no document-control fields and can create folders. */
  scope?: 'project' | 'workspace';
  canCreateFolder?: boolean;
  /** Native-storage scopes keep deleted items in a recycle bin. */
  canViewRecentlyDeleted?: boolean;
  canNavigateBack: boolean;
  canNavigateHome: boolean;
}

export interface DocumentPreviewHeaderState {
  fileName: string;
  isSaving: boolean;
  lastSavedAt: Date | null;
  canSave: boolean;
  proxyUrl: string | null;
  usesAdobe: boolean;
}

export interface DocumentsHeaderCtx {
  documentsHeaderState: DocumentsHeaderState;
  documentsSearchOpen: boolean;
  isDesktopViewport: boolean;
  /** setDocumentsSearchOpen — needed to open the morph from the pill click. */
  setDocumentsSearchOpen: (open: boolean) => void;
  /** dismissDocumentsSearch — clears value + broadcasts empty query. */
  dismissDocumentsSearch: () => void;
  /** documentsSearchInputRef — for programmatic focus on morph open. */
  documentsSearchInputRef: React.RefObject<HTMLInputElement | null>;
  /** Mutates in-place; caller owns the arrays. */
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
}

/** Appends Documents pills to the caller's leading/trailing action arrays.
 *  Called only when `documentsHeaderState` is non-null AND no preview is
 *  open (preview has its own slimmer action set). */
export function buildDocumentsHeaderActions(ctx: DocumentsHeaderCtx): void {
  const {
    documentsHeaderState,
    documentsSearchOpen,
    isDesktopViewport,
    setDocumentsSearchOpen,
    dismissDocumentsSearch,
    documentsSearchInputRef,
    leadingActions,
    trailingActions,
  } = ctx;

  const isWorkspace = documentsHeaderState.scope === 'workspace';

  // Phones have no path bar; the workspace page sits under a record, so the folder step back is its own pill.
  if (isWorkspace && !isDesktopViewport && documentsHeaderState.canNavigateBack) {
    leadingActions.push({
      id: 'documents-folder-up',
      icon: ChevronLeft,
      ariaLabel: 'Up one folder',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:navigate-back')),
    });
  }

  // Search pill — expands into morph input on tap (body placeholder bar fires
  // documents:open-search; the pill itself also toggles directly).
  leadingActions.push({
    id: 'documents-search',
    icon: Search,
    ariaLabel: isWorkspace ? 'Search documents' : 'Search project documents',
    onClick: () => {
      if (documentsSearchOpen) {
        dismissDocumentsSearch();
      } else {
        setDocumentsSearchOpen(true);
        requestAnimationFrame(() => documentsSearchInputRef.current?.focus());
      }
    },
  });

  // A desktop selection swaps the page pills for bulk Download and Delete until it clears.
  const selectedCount = documentsHeaderState.selectedCount ?? 0;
  if (isDesktopViewport && selectedCount > 0) {
    if (documentsHeaderState.canBulkDownload) {
      trailingActions.push({
        id: 'documents-bulk-download', icon: Download, ariaLabel: `Download ${selectedCount} files`,
        onClick: () => window.dispatchEvent(new CustomEvent('documents:bulk-download')),
        group: 'page',
      });
    }
    if (documentsHeaderState.canBulkDelete) {
      trailingActions.push({
        id: 'documents-bulk-delete', icon: Trash2, ariaLabel: `Delete ${selectedCount} files`,
        onClick: () => window.dispatchEvent(new CustomEvent('documents:bulk-delete')),
        group: 'page',
      });
    }
    return;
  }

  // Desktop filter and sort live on the Documents list header; only
  // mobile, which has no column header, keeps them as pills.
  if (isDesktopViewport) {
    trailingActions.push({
      id: 'documents-view-mode',
      icon: documentsHeaderState.viewMode === 'grid' ? List : LayoutGrid,
      ariaLabel: documentsHeaderState.viewMode === 'grid' ? 'List view' : 'Grid view',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:toggle-view-mode')),
      group: 'page',
    });


  }

  if (isDesktopViewport && documentsHeaderState.bulkSelectMode) {
    trailingActions.push({
      id: 'documents-cancel-selection', icon: CheckSquare, active: true,
      ariaLabel: 'Cancel selection',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:toggle-bulk-select')),
      group: 'page',
    });
  }

  // View-mode toggle — mobile keeps a standalone grid/list switch outside
  // search so the grid default isn't a one-way door (the desktop branch above
  // already carries its own within the sort/view/bulk cluster).
  if (!isDesktopViewport) {
    trailingActions.push({
      id: 'documents-view-mode',
      icon: documentsHeaderState.viewMode === 'grid' ? List : LayoutGrid,
      ariaLabel: documentsHeaderState.viewMode === 'grid' ? 'List view' : 'Grid view',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:toggle-view-mode')),
      group: 'page',
    });
  }

  // Hide folders, multi-select, permissions, tender letter and project root live in the
  // canvas on desktop (path bar, table filter menu, row checkboxes), revision history included.
  if (!isDesktopViewport && documentsHeaderState.hasRevisionHistory) {
    trailingActions.push({ id: 'documents-revision-history', icon: FileText, ariaLabel: 'Revision history', group: 'page', onClick: () => window.dispatchEvent(new CustomEvent('documents:revision-history')) });
  }

  if (documentsHeaderState.canCreateFolder) {
    trailingActions.push({
      id: 'documents-new-folder',
      icon: FolderPlus,
      ariaLabel: 'New folder',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:new-folder')),
      group: 'page',
    });
  }

  if (documentsHeaderState.canViewRecentlyDeleted) {
    trailingActions.push({
      id: 'documents-recently-deleted',
      icon: History,
      ariaLabel: 'Recently deleted',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:recently-deleted')),
      group: 'page',
    });
  }

  // Upload CTA — every viewport (field crews attach files from phones).
  if (documentsHeaderState.canUpload) {
    trailingActions.push({
      id: 'documents-upload',
      icon: documentsHeaderState.isUploading ? Loader2 : Plus,
      ariaLabel: documentsHeaderState.isUploading ? 'Uploading…' : 'Upload',
      onClick: () => window.dispatchEvent(new CustomEvent('documents:upload')),
      group: 'page',
    });
  }

  // Search-active mobile cluster: two pills (view+sort options + cancel X).
  // Only added on mobile when the morph is open. Matches iOS Files pattern.
  if (!isDesktopViewport && documentsSearchOpen) {
    type SearchSortKey = 'name' | 'type' | 'document_number' | 'date' | 'size';
    const sortKeyLabels: Record<SearchSortKey, string> = {
      name: 'Name',
      type: 'Kind',
      document_number: 'Document No.',
      date: 'Date',
      size: 'Size',
    };
    const sortKeyOrder: SearchSortKey[] = isWorkspace
      ? ['name', 'type', 'date', 'size']
      : ['name', 'type', 'document_number', 'date', 'size'];
    const allowedSortKeys: readonly SearchSortKey[] = sortKeyOrder;
    const activeSort: SearchSortKey = (allowedSortKeys as readonly string[]).includes(
      documentsHeaderState.sortBy,
    )
      ? (documentsHeaderState.sortBy as SearchSortKey)
      : 'name';
    const direction = documentsHeaderState.sortDirection ?? 'asc';
    const isGrid = documentsHeaderState.viewMode === 'grid';

    const viewSubActions: MobileHeaderSubAction[] = [
      {
        id: 'documents-search-view-icons',
        icon: LayoutGrid,
        label: 'Icons',
        trailingIcon: isGrid ? Check : undefined,
        onClick: () => {
          if (!isGrid) window.dispatchEvent(new CustomEvent('documents:toggle-view-mode'));
        },
      },
      {
        id: 'documents-search-view-list',
        icon: List,
        label: 'List',
        trailingIcon: !isGrid ? Check : undefined,
        onClick: () => {
          if (isGrid) window.dispatchEvent(new CustomEvent('documents:toggle-view-mode'));
        },
      },
    ];

    const sortSubActions: MobileHeaderSubAction[] = sortKeyOrder.map((key) => ({
      id: `documents-search-sort-${key}`,
      icon: ArrowUpDown,
      label: sortKeyLabels[key],
      sublabel: key === activeSort ? (direction === 'asc' ? 'Ascending' : 'Descending') : undefined,
      trailingIcon: key === activeSort ? Check : undefined,
      onClick: () => window.dispatchEvent(
        new CustomEvent('documents:set-sort', { detail: { sort: key } }),
      ),
    }));

    trailingActions.push({
      id: 'documents-search-options',
      icon: isGrid ? LayoutGrid : List,
      ariaLabel: `View and sort options. View: ${isGrid ? 'Icons' : 'List'}. Sort: ${sortKeyLabels[activeSort]} ${direction === 'asc' ? 'ascending' : 'descending'}.`,
      group: 'page',
      subActions: [...viewSubActions, ...sortSubActions],
      subActionDividerAfter: 'documents-search-view-list',
    });

    trailingActions.push({
      id: 'documents-search-cancel',
      icon: X,
      ariaLabel: 'Cancel search',
      onClick: () => dismissDocumentsSearch(),
      group: 'page',
    });
  }
}
